<script setup>
    import { ref, computed } from "vue";
    import { useStore } from 'vuex';
    import { storage } from '@/firebase';
    import { ref as firebaseRef, uploadBytes, getDownloadURL } from 'firebase/storage';
    import ImageSelector from '@/components/modals/ImageSelectorModal.vue';
    import VerifyPhoneNumber from '@/components/modals/VerifyPhoneNumber.vue';
    import InternationalPhoneInput from '@/components/common/InternationalPhoneInput.vue';
    import PWAInstallButton from '@/components/common/PWAInstallButton.vue';
    import Users from '@/api/users';
    // import LoadingAnimation3 from '@/components/loadingIcons/LoadingAnimation3.vue';

    // Grab stored values
    const store = useStore();
    const user = computed(() => store.state.user);
    const firstName = ref(user.value.firstName);
    const lastName = ref(user.value.lastName);
    const email = ref(user.value.email);
    const phone = ref(user.value.phone ? String(user.value.phone) : '');
    const phoneValidation = ref({ valid: true });
    const modal = ref('');
    const edit = ref("");
    const saving = ref(false);

    const uploadImage = async (folder, file) => {
        if (!file) return "";
        let imageURL = null;
        const storageRef = firebaseRef(storage, `${folder}/${user.value.uid}`);
        await uploadBytes(storageRef, file).then(() => {
            return getDownloadURL(storageRef);
        }).then((url) => {
            imageURL = url;
        });
        
        // Create a new user object with the updated profile picture
        const updatedUser = { ...user.value, profilePic: imageURL };
        store.dispatch('setUser', updatedUser);
        return imageURL;
    };

    // Handle the cropped image from the modal
    const handleImageCropped = async (croppedFile) => {
        try {
            // Upload the file directly - no conversion needed!
            await uploadImage('profile', croppedFile);
            
            // Close the modal
            modal.value = '';
        } catch (error) {
            console.error('Error uploading profile image:', error);
        }
    }; 

    const handlePhoneValidation = (validation) => {
        phoneValidation.value = validation;
    };

    const saveChanges = async () => {
        if (!phoneValidation.value.valid && phone.value) {
            alert('Please enter a valid phone number before saving.');
            return;
        }

        if (phone.value !== user.value.phone) {
            modal.value = 'verify';
        }

        saving.value = true;
        try {
            const response = await Users.put('', {
                userUID: user.value.uid,
                firstName: firstName.value,
                lastName: lastName.value,
                email: email.value,
            });

            if (response.status === 'success') {
                const updatedUser = {
                    ...user.value,
                    firstName: firstName.value,
                    lastName: lastName.value,
                    email: email.value
                };
                store.dispatch('setUser', updatedUser);
            }
            edit.value = "";
        } catch (error) {
            console.error('Failed to save profile:', error);
        } finally {
            saving.value = false;
        }
    };

    const saveNotificationChanges = async (notification, type) => {
        if (type === 'sms' && user.value.phone === '') {
            modal.value = 'verify';
            user.value.notifications[notification]['smsEnable'] = false;
            return;
        }

        const response = await Users.put('', {
            userUID: user.value.uid,
            notifications: {
                [notification]: {
                    emailEnable: user.value.notifications[notification].emailEnable,
                    smsEnable: user.value.notifications[notification].smsEnable,
                }
            },
        });
        if (response.status === 'success') {
            store.dispatch('setUser', {
                ...user.value,
                notifications: user.value.notifications,
            });
        }
    }
</script>

<template>
    <h1 class="text-2xl font-bold text-mc-ink dark:text-on-dark mb-4 md:mb-6">Profile</h1>
    <div v-if="user" class="flex-grow overflow-y-auto px-2 md:px-0">
        <!-- User Info Section -->
        <div class="flex flex-col mb-6 items-center bg-mc-white dark:bg-white/[0.05] border border-mc-steel dark:border-line-on-dark p-4 rounded-mc-md">
            <div class="flex flex-col md:flex-row mb-6 items-center justify-between w-full border-b border-mc-steel dark:border-line-on-dark pb-4">
                <p class="text-sm font-semibold text-ink-muted dark:text-on-dark-muted">Details</p>
                <div class="flex flex-row gap-2">
                    <button v-if="edit === 'details'" class="default-button" @click="edit = ''">
                        Cancel
                    </button>
                    <button v-if="edit === 'details'" class="default-button" @click="saveChanges" :disabled="saving">
                        <span v-if="saving" class="inline-flex items-center gap-1.5">
                            <span class="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
                            Saving...
                        </span>
                        <span v-else>Save</span>
                    </button>
                    <button v-if="edit !== 'details'" class="default-button" @click="edit = 'details'">
                        <font-awesome-icon icon="edit" class="mr-2"/>
                        Edit
                    </button>
                </div>
            </div>
            <div class="flex flex-col md:flex-row mb-6 items-center w-full">
                <div class="relative w-36 md:w-52 h-36 md:h-52 mb-4 md:mb-8 mr-4 border border-mc-steel dark:border-line-on-dark rounded-mc-sm overflow-hidden">
                    <img v-if="user.profilePic" :src=user.profilePic alt="profile image" class="w-full h-full object-cover">
                    <img v-else src="@/assets/images/default-avatar.png" alt="profile image" class="w-full h-full object-cover">

                    <!-- Overlay button for uploading photo -->
                    <button
                        v-if="edit === 'details'"
                        class="absolute bottom-2 left-1/2 transform -translate-x-1/2 default-button w-10/12"
                        @click="modal = 'image'"
                    >
                        {{ user.profilePic ? 'Change Photo' : 'Upload Photo' }}
                    </button>
                </div>
                <div class="w-full">
                    <div class="flex flex-col md:flex-row md:items-center mb-5">
                        <!-- First Name Input with Floating Label -->
                        <div class="relative w-full md:w-1/2 mr-2">
                            <input 
                                 type="text" 
                                 v-model="firstName" 
                                 id="firstName"
                                 class="w-full rounded-mc-sm p-2 pt-6 pb-2 border border-mc-steel dark:border-line-on-dark focus:border-mc-blue focus:outline-none focus:ring-[3px] focus:ring-mc-blue/40 transition-colors duration-160 text-mc-ink dark:text-on-dark"
                                 :class="edit === 'details' ? 'bg-mc-cloud dark:bg-mc-charcoal' : 'bg-mc-cloud dark:bg-white/[0.05]'"
                                 placeholder=" "
                                 :disabled="edit !== 'details'"
                             >
                            <label
                                for="firstName"
                                class="absolute left-2 top-1 text-xs text-ink-muted dark:text-on-dark-muted font-medium pointer-events-none transition-all duration-200"
                            >
                                First Name
                            </label>
                        </div>
                        
                        <!-- Last Name Input with Floating Label -->
                        <div class="relative w-full md:w-1/2">
                            <input 
                                 type="text" 
                                 v-model="lastName" 
                                 id="lastName"
                                 class="w-full rounded-mc-sm p-2 pt-6 pb-2 border border-mc-steel dark:border-line-on-dark focus:border-mc-blue focus:outline-none focus:ring-[3px] focus:ring-mc-blue/40 transition-colors duration-160 text-mc-ink dark:text-on-dark"
                                 :class="edit === 'details' ? 'bg-mc-cloud dark:bg-mc-charcoal' : 'bg-mc-cloud dark:bg-white/[0.05]'"
                                 placeholder=" "
                                 :disabled="edit !== 'details'"
                             >
                            <label
                                for="lastName"
                                class="absolute left-2 top-1 text-xs text-ink-muted dark:text-on-dark-muted font-medium pointer-events-none transition-all duration-200"
                            >
                                Last Name
                            </label>
                        </div>
                    </div>
                    
                    <div class="flex flex-col md:flex-row md:items-center mb-5">
                        <!-- Email Input with Floating Label -->
                        <div class="relative w-full md:w-1/2 mr-2">
                            <input 
                                 type="text" 
                                 v-model="email" 
                                 id="email"
                                 class="w-full rounded-mc-sm p-2 pt-6 pb-2 border border-mc-steel dark:border-line-on-dark focus:border-mc-blue focus:outline-none focus:ring-[3px] focus:ring-mc-blue/40 transition-colors duration-160 text-mc-ink dark:text-on-dark"
                                 :class="edit === 'details' ? 'bg-mc-cloud dark:bg-mc-charcoal' : 'bg-mc-cloud dark:bg-white/[0.05]'"
                                 placeholder=" "
                                 :disabled="edit !== 'details'"
                             >
                            <label
                                for="email"
                                class="absolute left-2 top-1 text-xs text-ink-muted dark:text-on-dark-muted font-medium pointer-events-none transition-all duration-200"
                            >
                                Email
                            </label>
                        </div>

                        <!-- International Phone Number Input -->
                        <div class="w-full md:w-1/2">
                            <InternationalPhoneInput
                                v-model="phone"
                                :disabled="edit !== 'details'"
                                placeholder="Enter phone number"
                                label="Phone Number"
                                input-id="phone"
                                :default-country="'US'"
                                @validate="handlePhoneValidation"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Notification preferences -->
        <div class="flex flex-col mb-6 items-center bg-mc-white dark:bg-white/[0.05] border border-mc-steel dark:border-line-on-dark p-4 rounded-mc-md">
            <div class="flex flex-col md:flex-row mb-6 items-center justify-between w-full border-b border-mc-steel dark:border-line-on-dark pb-4">
                <p class="text-sm font-semibold text-ink-muted dark:text-on-dark-muted">Notification Preferences</p>
            </div>
            <div v-for="notification in Object.keys(user.notifications)" :key="notification" class="flex items-center justify-between mb-6 w-full">
                <p class="text-sm font-semibold text-mc-ink dark:text-on-dark">{{ notification }}</p>
                <div class="flex flex-row gap-6">
                    <!-- Email toggle -->
                    <div class="flex flex-col items-center gap-1">
                        <span class="text-xs text-ink-muted dark:text-on-dark-muted">Email</span>
                        <input
                            :id="`emailToggle-${notification}`"
                            type="checkbox"
                            v-model="user.notifications[notification].emailEnable"
                            class="toggle-checkbox hidden"
                            @change="saveNotificationChanges(notification, 'email')"
                        />
                        <label
                            :for="`emailToggle-${notification}`"
                            class="toggle-label block w-12 h-6 rounded-full cursor-pointer relative"
                            :class="user.notifications[notification].emailEnable ? 'bg-green-500' : 'bg-mc-steel dark:bg-white/20'"
                        >
                            <span
                                class="absolute left-0 top-0 w-6 h-6 bg-white border border-gray-300 rounded-full transition-transform duration-200"
                                :class="user.notifications[notification].emailEnable ? 'translate-x-6' : ''"
                            ></span>
                        </label>
                    </div>
                    <!-- SMS toggle -->
                    <div class="flex flex-col items-center gap-1">
                        <span class="text-xs text-ink-muted dark:text-on-dark-muted">SMS</span>
                        <input
                            :id="`smsToggle-${notification}`"
                            type="checkbox"
                            v-model="user.notifications[notification].smsEnable"
                            class="toggle-checkbox hidden"
                            @change="saveNotificationChanges(notification, 'sms')"
                        />
                        <label
                            :for="`smsToggle-${notification}`"
                            class="toggle-label block w-12 h-6 rounded-full cursor-pointer relative"
                            :class="user.notifications[notification].smsEnable ? 'bg-green-500' : 'bg-mc-steel dark:bg-white/20'"
                        >
                            <span
                                class="absolute left-0 top-0 w-6 h-6 bg-white border border-gray-300 rounded-full transition-transform duration-200"
                                :class="user.notifications[notification].smsEnable ? 'translate-x-6' : ''"
                            ></span>
                        </label>
                    </div>
                </div>
            </div>
        </div>

        <!-- Mobile App Section -->
        <div class="flex flex-col mb-6 items-center bg-mc-white dark:bg-white/[0.05] border border-mc-steel dark:border-line-on-dark p-4 rounded-mc-md">
            <div class="flex flex-col md:flex-row mb-6 items-center justify-between w-full border-b border-mc-steel dark:border-line-on-dark pb-4">
                <p class="text-sm font-semibold text-ink-muted dark:text-on-dark-muted">Mobile App</p>
            </div>
            <div class="w-full space-y-4">
                <div class="flex flex-col space-y-2">
                    <p class="text-sm text-ink-muted dark:text-on-dark-muted">
                        Install MeshPortal as a mobile app on your device for quick access from your home screen.
                    </p>
                    <div class="flex justify-center md:justify-start">
                        <PWAInstallButton 
                            :always-show="true" 
                            variant="settings"
                        />
                    </div>
                </div>
                <div class="text-xs text-ink-muted dark:text-on-dark-muted space-y-1">
                    <p><strong>Benefits:</strong></p>
                    <ul class="list-disc list-inside ml-2">
                        <li>Quick access from your home screen</li>
                        <li>Native app experience</li>
                        <li>Automatic updates</li>
                        <li>No app store needed</li>
                    </ul>
                </div>
            </div>
        </div>
    </div>

    <ImageSelector 
        v-if="modal === 'image'" 
        :initial-image="user.profilePic"
        @close="modal = ''" 
        @image-cropped="handleImageCropped"
    />

    <VerifyPhoneNumber
        v-if="modal === 'verify'"
        :phoneNumber="phone"
        @close="modal = ''"
    />
</template>
